import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { AIUsageSummary } from '../../services/AIUsageService';

interface AIUsageChartsProps {
  summary: AIUsageSummary;
  period: number;
}

const AIUsageCharts: React.FC<AIUsageChartsProps> = ({ summary }) => {
  // Prepare daily cost data
  const dailyCostData = React.useMemo(() => {
    const claudeData = summary.claude.cost?.daily_costs || [];
    const openaiData = summary.openai.usage?.daily_data || [];

    // Merge data by date
    const dataMap = new Map();

    claudeData.forEach(item => {
      const date = new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      dataMap.set(item.date, {
        date,
        claude: item.cost_usd,
        openai: 0
      });
    });

    openaiData.forEach(item => {
      const date = new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const existing = dataMap.get(item.date) || { date, claude: 0, openai: 0 };
      existing.openai = item.cost_usd;
      dataMap.set(item.date, existing);
    });

    return Array.from(dataMap.values()).sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
  }, [summary]);

  // Prepare token usage data for Claude
  const tokenUsageData = React.useMemo(() => {
    if (!summary.claude.usage?.daily_usage) return [];

    return summary.claude.usage.daily_usage.map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      input: item.input_tokens,
      output: item.output_tokens,
      cache_creation: item.cache_creation_tokens,
      cache_read: item.cache_read_tokens
    }));
  }, [summary]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2
          }}
        >
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="caption" display="block" color={entry.color}>
              {entry.name}: {typeof entry.value === 'number' ?
                entry.value.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 4
                }) : entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Daily Cost Chart */}
      <Box sx={{ width: '100%' }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Daily Cost Breakdown
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyCostData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis
                  label={{ value: 'Cost (USD)', angle: -90, position: 'insideLeft' }}
                  tickFormatter={(value) => `$${value.toFixed(2)}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="claude"
                  name="Claude"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="openai"
                  name="OpenAI"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Token Usage Chart (Claude only) */}
      {tokenUsageData.length > 0 && (
        <Box sx={{ width: '100%' }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Claude Token Usage (Daily)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={tokenUsageData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis
                    label={{ value: 'Tokens', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="input" name="Input Tokens" fill="#3b82f6" stackId="a" />
                  <Bar dataKey="output" name="Output Tokens" fill="#8b5cf6" stackId="a" />
                  <Bar dataKey="cache_creation" name="Cache Creation" fill="#f59e0b" stackId="a" />
                  <Bar dataKey="cache_read" name="Cache Read" fill="#10b981" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Bottom row with two cards */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Cost Distribution */}
        <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '300px' }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cost Distribution
              </Typography>
              <Box sx={{ mt: 2 }}>
                {summary.claude.status === 'active' && summary.claude.cost && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Claude (Anthropic)</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      ${summary.claude.cost.total_cost_usd.toFixed(4)}
                      {' '}
                      ({((summary.claude.cost.total_cost_usd / summary.summary.total_cost_usd) * 100).toFixed(1)}%)
                    </Typography>
                  </Box>
                )}
                {summary.openai.status === 'active' && summary.openai.usage && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">OpenAI</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      ${summary.openai.usage.total_cost_usd.toFixed(4)}
                      {' '}
                      ({((summary.openai.usage.total_cost_usd / summary.summary.total_cost_usd) * 100).toFixed(1)}%)
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Usage Statistics */}
        <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '300px' }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Usage Statistics
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Active Providers</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {summary.summary.active_providers}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Average Daily Cost</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    ${summary.summary.average_daily_cost.toFixed(4)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Projected Monthly Cost</Typography>
                  <Typography variant="body2" fontWeight="bold" color="warning.main">
                    ${summary.summary.projected_monthly_cost.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default AIUsageCharts;
